import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import {
  applyCustomerCsvMapping,
  CustomerCsvFileStructure,
  CustomerCsvImportRow,
  CustomerCsvParseResult,
  CustomerPriceGroupOption,
  formatPriceGroupPreview,
  getCustomerCsvFieldOptions,
  getCustomerCsvPreviewColumns,
  mappedFieldsFromColumnMapping,
  parseCustomerCsvStructure,
  toCustomerImportPayload,
  validateCustomerCsvPriceGroups,
} from '../../../utils/customer-csv.util';

export interface CustomerImportResult {
  created: number;
  failed: number;
}

@Component({
  selector: 'app-duna-customers-import-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  template: `
    <h2 mat-dialog-title>Importer kunder fra CSV</h2>

    <mat-dialog-content>
      @if (priceGroupsLoading) {
      <div class="loading-section">
        <mat-progress-spinner diameter="32" mode="indeterminate"></mat-progress-spinner>
        <p>Henter prisgrupper…</p>
      </div>
      } @else if (priceGroupsError) {
      <p class="error-msg"><mat-icon>error</mat-icon> {{ priceGroupsError }}</p>
      } @else if (!parseResult) {
      <div class="upload-section">
        <p class="intro">
          Vælg en CSV-fil med kundedata. Du får en forhåndsvisning af felterne,
          inden data gemmes.
        </p>
        <div class="upload-box">
          <mat-icon>upload_file</mat-icon>
          <p>Vælg CSV-fil</p>
          <input
            #fileInput
            type="file"
            accept=".csv,text/csv"
            (change)="onFileSelected($event)"
          />
        </div>
        <p class="hint">
          Første række skal være kolonneoverskrifter. Navn mappes automatisk
          hvor det genkendes; du kan rette matching i forhåndsvisningen.
          Separator kan være komma, semikolon eller tab.
        </p>
        <p class="hint">
          Prisgruppe kan være tom, "Ingen", prisgruppens navn eller id.
          @if (priceGroups.length) {
          Gyldige prisgrupper: {{ priceGroupLabelsHint }}.
          } @else {
          Organisationen har ingen prisgrupper – lad kolonnen være tom.
          }
        </p>
        @if (fileError) {
        <p class="error-msg"><mat-icon>error</mat-icon> {{ fileError }}</p>
        }
      </div>
      } @else {
      <div class="preview-section">
        <div class="summary">
          <mat-chip-set>
            <mat-chip highlighted color="primary">
              {{ validRows.length }} klar til import
            </mat-chip>
            @if (invalidRows.length) {
            <mat-chip highlighted color="warn">
              {{ invalidRows.length }} springes over
            </mat-chip>
            }
          </mat-chip-set>
          <button mat-stroked-button type="button" (click)="resetFile()">
            <mat-icon>folder_open</mat-icon>
            Vælg anden fil
          </button>
        </div>

        @if (csvStructure?.headers.length) {
        <div class="column-mapping">
          <p class="mapping-title">Match kolonner fra CSV</p>
          <p class="hint">
            Vælg hvilket kundefelt hver kolonne i filen skal importeres til.
            Forhåndsvisningen opdateres med det samme.
          </p>
          <div class="mapping-grid">
            @for (header of csvStructure!.headers; track $index) {
            <span class="csv-header" [title]="header">{{ header }}</span>
            <mat-icon class="mapping-arrow">arrow_forward</mat-icon>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-select
                [value]="columnMappingValue($index)"
                (selectionChange)="onColumnMappingChange($index, $event.value)"
              >
                <mat-option value="">Ignorer</mat-option>
                @for (opt of fieldOptions; track opt.key) {
                <mat-option [value]="opt.key">
                  {{ opt.label }}
                  @if (fieldMappedByOtherColumn(opt.key, $index)) {
                  <span
                    style="color:#ed6c02;font-size:0.8125rem;font-weight:400"
                  >
                    · bruges af «{{
                      fieldMappedByOtherHeader(opt.key, $index)
                    }}»
                  </span>
                  }
                </mat-option>
                }
              </mat-select>
            </mat-form-field>
            }
          </div>
          @if (!hasNameMapped) {
          <p class="warning-msg">
            <mat-icon>warning</mat-icon>
            Kolonnen <strong>Navn</strong> skal mappes for at importere kunder.
          </p>
          }
        </div>
        }

        @if (mappedFieldLabels.length) {
        <p class="mapped-fields">
          <strong>Aktive felter:</strong>
          {{ mappedFieldLabels.join(', ') }}
        </p>
        }

        <div class="preview-table-wrap">
          <table mat-table [dataSource]="parseResult.rows" class="preview-table">
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let row">
                @if (row.isValid) {
                <mat-icon class="status-icon ok">check_circle</mat-icon>
                } @else {
                <mat-icon class="status-icon warn">error</mat-icon>
                }
              </td>
            </ng-container>

            @for (col of previewColumns; track col.key) {
            <ng-container [matColumnDef]="col.key">
              <th mat-header-cell *matHeaderCellDef>{{ col.label }}</th>
              <td mat-cell *matCellDef="let row">
                @if (col.key === 'price_group') {
                {{ formatPriceGroupPreview(row) }}
                } @else {
                {{ row[col.key] || '—' }}
                }
              </td>
            </ng-container>
            }

            <ng-container matColumnDef="error">
              <th mat-header-cell *matHeaderCellDef>Fejl</th>
              <td mat-cell *matCellDef="let row" class="error-cell">
                {{ row.error || '—' }}
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr
              mat-row
              *matRowDef="let row; columns: displayedColumns"
              [class.invalid-row]="!row.isValid"
            ></tr>
          </table>
        </div>

        @if (importError) {
        <p class="error-msg"><mat-icon>error</mat-icon> {{ importError }}</p>
        } @if (importSummary) {
        <p class="success-msg">
          <mat-icon>check_circle</mat-icon>
          {{ importSummary.created }} kunder importeret
          @if (importSummary.failed) {
          , {{ importSummary.failed }} fejlede
          }
        </p>
        }
      </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="onClose()" [disabled]="isImporting">
        {{ importSummary ? 'Luk' : 'Annuller' }}
      </button>
      @if (parseResult && !importSummary) {
      <button
        mat-raised-button
        color="primary"
        [disabled]="isImporting || validRows.length === 0 || !hasNameMapped"
        (click)="onImport()"
      >
        @if (isImporting) {
        <mat-progress-spinner
          diameter="18"
          mode="indeterminate"
          style="display:inline-block;margin-right:6px"
        ></mat-progress-spinner>
        }
        Importer {{ validRows.length }} kunder
      </button>
      }
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      mat-dialog-content {
        min-width: min(960px, 92vw);
        max-height: 70vh;
      }

      .loading-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 32px 0;
        color: rgba(0, 0, 0, 0.6);
      }

      .upload-section,
      .preview-section {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-top: 8px;
      }

      .intro,
      .hint,
      .mapped-fields {
        margin: 0;
        color: rgba(0, 0, 0, 0.7);
        font-size: 0.9rem;
        line-height: 1.5;
      }

      .upload-box {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 32px;
        border: 2px dashed rgba(0, 0, 0, 0.2);
        border-radius: 8px;
        cursor: pointer;
        transition: border-color 0.2s, background 0.2s;

        &:hover {
          border-color: var(--mat-sys-primary, #3f51b5);
          background: rgba(63, 81, 181, 0.04);
        }

        mat-icon {
          font-size: 40px;
          width: 40px;
          height: 40px;
          color: rgba(0, 0, 0, 0.45);
        }

        input[type='file'] {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }
      }

      .summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;
      }

      .column-mapping {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px 14px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.02);
      }

      .mapping-title {
        margin: 0;
        font-weight: 600;
        font-size: 0.95rem;
      }

      .mapping-grid {
        display: grid;
        grid-template-columns: minmax(120px, 1fr) auto minmax(160px, 220px);
        gap: 8px 12px;
        align-items: center;
      }

      .csv-header {
        font-size: 0.875rem;
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .mapping-arrow {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: rgba(0, 0, 0, 0.45);
      }

      .mapping-grid mat-form-field {
        width: 100%;
        margin: 0;
      }

      .preview-table-wrap {
        overflow: auto;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 4px;
        max-height: 420px;
      }

      .preview-table {
        width: 100%;
        min-width: 900px;
      }

      .invalid-row {
        background: rgba(244, 67, 54, 0.06);
      }

      .error-cell {
        color: #c62828;
        font-size: 0.8125rem;
        max-width: 280px;
        white-space: normal;
      }

      .status-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;

        &.ok {
          color: #4caf50;
        }

        &.warn {
          color: #f44336;
        }
      }

      .error-msg,
      .warning-msg,
      .success-msg {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        font-size: 0.875rem;

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
        }
      }

      .error-msg {
        color: #f44336;
      }

      .warning-msg {
        color: #ed6c02;
      }

      .success-msg {
        color: #2e7d32;
      }
    `,
  ],
})
export class DunaCustomersImportDialogComponent implements OnInit {
  parseResult: CustomerCsvParseResult | null = null;
  csvStructure: CustomerCsvFileStructure | null = null;
  columnMapping: (string | null)[] = [];
  fieldOptions = getCustomerCsvFieldOptions();
  fileError: string | null = null;
  importError: string | null = null;
  importSummary: CustomerImportResult | null = null;
  isImporting = false;
  priceGroupsLoading = true;
  priceGroupsError: string | null = null;
  priceGroups: CustomerPriceGroupOption[] = [];

  previewColumns = getCustomerCsvPreviewColumns();
  displayedColumns = [
    'status',
    ...this.previewColumns.map((col) => col.key),
    'error',
  ];
  formatPriceGroupPreview = formatPriceGroupPreview;

  constructor(
    public dialogRef: MatDialogRef<
      DunaCustomersImportDialogComponent,
      CustomerImportResult | false
    >,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadPriceGroups();
  }

  get priceGroupLabelsHint(): string {
    return this.priceGroups.map((group) => group.label).join(', ');
  }

  get validRows(): CustomerCsvImportRow[] {
    return this.parseResult?.rows.filter((row) => row.isValid) ?? [];
  }

  get invalidRows(): CustomerCsvImportRow[] {
    return this.parseResult?.rows.filter((row) => !row.isValid) ?? [];
  }

  get mappedFieldLabels(): string[] {
    const labelByKey = new Map(
      this.previewColumns.map((col) => [col.key, col.label])
    );
    return (this.parseResult?.mappedFields ?? [])
      .map((key) => labelByKey.get(key as keyof CustomerCsvImportRow) ?? key)
      .filter((label, index, arr) => arr.indexOf(label) === index);
  }

  get hasNameMapped(): boolean {
    return this.columnMapping.includes('name');
  }

  columnMappingValue(index: number): string {
    return this.columnMapping[index] ?? '';
  }

  fieldMappedByOtherColumn(fieldKey: string, columnIndex: number): boolean {
    if (!fieldKey) return false;
    return this.columnMapping.some(
      (field, index) => index !== columnIndex && field === fieldKey
    );
  }

  fieldMappedByOtherHeader(fieldKey: string, columnIndex: number): string {
    const otherIndex = this.columnMapping.findIndex(
      (field, index) => index !== columnIndex && field === fieldKey
    );
    if (otherIndex < 0 || !this.csvStructure) return '';
    return (
      this.csvStructure.headers[otherIndex]?.trim() ||
      `kolonne ${otherIndex + 1}`
    );
  }

  onColumnMappingChange(index: number, value: string): void {
    if (!this.csvStructure) return;
    const next = [...this.columnMapping];
    next[index] = value === '' ? null : value;
    this.columnMapping = next;
    this.rebuildPreviewFromMapping();
  }

  private rebuildPreviewFromMapping(): void {
    if (!this.csvStructure) return;

    const rows = applyCustomerCsvMapping(
      this.csvStructure.dataLines,
      this.columnMapping
    );

    this.parseResult = {
      headers: this.csvStructure.headers,
      mappedFields: mappedFieldsFromColumnMapping(this.columnMapping),
      rows: validateCustomerCsvPriceGroups(rows, this.priceGroups),
    };
  }

  private loadPriceGroups(): void {
    this.priceGroupsLoading = true;
    this.priceGroupsError = null;

    this.http
      .get<{ items?: CustomerPriceGroupOption[] }>(
        `${environment.apiUrl}/events/price-groups`,
        { params: { activeOnly: 'true' } }
      )
      .subscribe({
        next: (res) => {
          this.priceGroups = (res?.items ?? [])
            .map((group) => ({
              id: Number(group.id),
              label: String(group.label ?? '').trim(),
            }))
            .filter((group) => Number.isFinite(group.id) && group.id > 0 && group.label);
          this.priceGroupsLoading = false;
        },
        error: () => {
          this.priceGroupsLoading = false;
          this.priceGroupsError =
            'Kunne ikke hente prisgrupper. Import er deaktiveret indtil prisgrupper kan valideres.';
          this.priceGroups = [];
        },
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.priceGroupsError) return;

    this.fileError = null;
    this.importError = null;
    this.importSummary = null;

    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result ?? '');
      const structure = parseCustomerCsvStructure(content);

      if (structure.headers.length === 0 || structure.dataLines.length === 0) {
        this.fileError =
          'Filen indeholder ingen gyldige datarækker. Kontrollér format og kolonneoverskrifter.';
        this.parseResult = null;
        this.csvStructure = null;
        this.columnMapping = [];
        return;
      }

      this.csvStructure = structure;
      this.columnMapping = [...structure.columnMapping];
      this.rebuildPreviewFromMapping();
    };
    reader.onerror = () => {
      this.fileError = 'Kunne ikke læse filen. Prøv igen.';
    };
    reader.readAsText(file, 'UTF-8');
  }

  resetFile(): void {
    this.parseResult = null;
    this.csvStructure = null;
    this.columnMapping = [];
    this.fileError = null;
    this.importError = null;
    this.importSummary = null;
  }

  onClose(): void {
    if (this.importSummary && this.importSummary.created > 0) {
      this.dialogRef.close(this.importSummary);
      return;
    }
    this.dialogRef.close(false);
  }

  onImport(): void {
    if (this.isImporting || !this.validRows.length) return;

    this.isImporting = true;
    this.importError = null;

    const payload = {
      customers: this.validRows.map((row) => toCustomerImportPayload(row)),
    };

    this.http
      .post<{ created: number; failed: number }>(
        `${environment.apiUrl}/organizations/customers/import`,
        payload
      )
      .subscribe({
        next: (result) => {
          this.isImporting = false;
          this.importSummary = {
            created: result.created ?? 0,
            failed: result.failed ?? 0,
          };
        },
        error: (err) => {
          this.isImporting = false;
          this.importError =
            err?.error?.message || 'Import fejlede. Prøv igen.';
        },
      });
  }
}
